import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Briefcase, Construction, FileText, HeartPulse, Truck, UtensilsCrossed,
  GraduationCap, Users2, Upload, Plus,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { api } from '@/lib/api'
import { formatPayrollMoney } from '@/features/payroll/format'
import { formatDate } from '@/lib/locale'

const tk = (tenantId: string, ...parts: unknown[]) => ['tenant', tenantId, ...parts]

function useTenantQuery<T>(path: string) {
  const { user } = useAuth()
  const tid = user?.tenantId
  return useQuery({
    queryKey: tk(tid ?? 'anon', path),
    queryFn: () => api.get<T>(path),
    enabled: Boolean(tid),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function HealthcareCredentialsPage() {
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; credential_kind: string; credential_number: string | null; issued_date: string | null; expiry_date: string | null; first_name: string; last_name: string; employee_number: string; verified_at: string | null }> }>('/api/payroll/industry/healthcare/credentials')
  const rows = data?.data ?? []
  return (
    <Page title="Healthcare Credentials" description="Track RN/MD/NP/PA/CNA/EMT credentials, issuing authority, and expiry. Expiring-soon employees alert in Smart Chat.">
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'employee_number', header: 'Employee', cell: ({ row }: { row: { original: { first_name: string; last_name: string; employee_number: string } } }) => `${row.original.first_name} ${row.original.last_name} (${row.original.employee_number})` },
          { accessorKey: 'credential_kind', header: 'Credential', cell: ({ row }: { row: { original: { credential_kind: string } } }) => <Badge variant="outline">{row.original.credential_kind}</Badge> },
          { accessorKey: 'credential_number', header: 'Number' },
          { accessorKey: 'issued_date', header: 'Issued', cell: ({ row }: { row: { original: { issued_date: string | null } } }) => row.original.issued_date ? formatDate(row.original.issued_date) : '-' },
          { accessorKey: 'expiry_date', header: 'Expiry', cell: ({ row }: { row: { original: { expiry_date: string | null } } }) => row.original.expiry_date ? formatDate(row.original.expiry_date) : '-' },
          { accessorKey: 'verified_at', header: 'Verified', cell: ({ row }: { row: { original: { verified_at: string | null } } }) => row.original.verified_at ? <Badge variant="success">Yes</Badge> : <Badge variant="outline">No</Badge> },
        ] as never}
        empty={<EmptyState icon={HeartPulse} title="No credentials tracked yet" description="Add credentials for each clinical employee to track expirations + auto-block expired-credential shifts." />}
      />
    </Page>
  )
}

export function PrevailingWagePage() {
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; job_classification: string; hourly_wage_minor_units: string; fringe_benefits_minor_units: string; currency: string; effective_from: string; jurisdiction_name: string | null }> }>('/api/payroll/industry/construction/prevailing-wage')
  const rows = data?.data ?? []
  return (
    <Page title="Prevailing Wage Rates" description="Davis-Bacon prevailing wage rates by classification. Used for certified payroll on federal-contractor jobs.">
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'job_classification', header: 'Job classification' },
          { accessorKey: 'jurisdiction_name', header: 'Jurisdiction' },
          { accessorKey: 'hourly_wage_minor_units', header: 'Hourly wage', cell: ({ row }: { row: { original: { hourly_wage_minor_units: string; currency: string } } }) => formatPayrollMoney(row.original.hourly_wage_minor_units, row.original.currency) },
          { accessorKey: 'fringe_benefits_minor_units', header: 'Fringe', cell: ({ row }: { row: { original: { fringe_benefits_minor_units: string; currency: string } } }) => formatPayrollMoney(row.original.fringe_benefits_minor_units, row.original.currency) },
          { accessorKey: 'effective_from', header: 'Effective', cell: ({ row }: { row: { original: { effective_from: string } } }) => formatDate(row.original.effective_from) },
        ] as never}
        empty={<EmptyState icon={Construction} title="No prevailing wage rates yet" description="Upload Davis-Bacon wage determinations from DOL for your federal-contractor work." />}
      />
    </Page>
  )
}

export function DotDriverHoursPage() {
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; log_date: string; on_duty_hours: string; driving_hours: string; rest_hours: string; mileage: string; cdl_class: string | null; hos_violation: boolean; first_name: string; last_name: string }> }>('/api/payroll/industry/transportation/driver-hours')
  const rows = data?.data ?? []
  return (
    <Page title="DOT Driver Hours" description="FMCSA hours-of-service tracking. Flags HOS violations automatically.">
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'log_date', header: 'Date', cell: ({ row }: { row: { original: { log_date: string } } }) => formatDate(row.original.log_date) },
          { accessorKey: 'first_name', header: 'Driver', cell: ({ row }: { row: { original: { first_name: string; last_name: string } } }) => `${row.original.first_name} ${row.original.last_name}` },
          { accessorKey: 'on_duty_hours', header: 'On duty' },
          { accessorKey: 'driving_hours', header: 'Driving' },
          { accessorKey: 'rest_hours', header: 'Rest' },
          { accessorKey: 'mileage', header: 'Miles' },
          { accessorKey: 'cdl_class', header: 'CDL' },
          { accessorKey: 'hos_violation', header: 'HOS', cell: ({ row }: { row: { original: { hos_violation: boolean } } }) => row.original.hos_violation ? <Badge variant="danger">Violation</Badge> : <Badge variant="success">OK</Badge> },
        ] as never}
        empty={<EmptyState icon={Truck} title="No driver hours logged" description="Drivers submit logs through the ESS portal or via ELD device integration." />}
      />
    </Page>
  )
}

export function TipPoolsPage() {
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; name: string; shift_date: string; total_tips_minor_units: string; total_service_charges_minor_units: string; currency: string }> }>('/api/payroll/industry/hospitality/tip-pools')
  const rows = data?.data ?? []
  return (
    <Page title="Tip Pools" description="Shift tip pooling: declared tips, charged tips, service charges, and Form 8027 allocation.">
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'shift_date', header: 'Shift', cell: ({ row }: { row: { original: { shift_date: string } } }) => formatDate(row.original.shift_date) },
          { accessorKey: 'name', header: 'Pool' },
          { accessorKey: 'total_tips_minor_units', header: 'Tips', cell: ({ row }: { row: { original: { total_tips_minor_units: string; currency: string } } }) => formatPayrollMoney(row.original.total_tips_minor_units, row.original.currency) },
          { accessorKey: 'total_service_charges_minor_units', header: 'Service charges', cell: ({ row }: { row: { original: { total_service_charges_minor_units: string; currency: string } } }) => formatPayrollMoney(row.original.total_service_charges_minor_units, row.original.currency) },
        ] as never}
        empty={<EmptyState icon={UtensilsCrossed} title="No tip pools yet" description="Create a pool per shift; assign hours-worked to participants and the engine allocates declared vs charged tips." />}
      />
    </Page>
  )
}

export function AcademicContractsPage() {
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; contract_kind: string; months_active: number; payout_months: number; annual_salary_minor_units: string; currency: string; academic_year_start: string; academic_year_end: string; first_name: string; last_name: string; employee_number: string }> }>('/api/payroll/industry/education/contracts')
  const rows = data?.data ?? []
  return (
    <Page title="Academic Contracts" description="9-month / 10-month / adjunct contracts with 12-month payout spread.">
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'employee_number', header: 'Employee', cell: ({ row }: { row: { original: { first_name: string; last_name: string; employee_number: string } } }) => `${row.original.first_name} ${row.original.last_name} (${row.original.employee_number})` },
          { accessorKey: 'contract_kind', header: 'Kind', cell: ({ row }: { row: { original: { contract_kind: string } } }) => <Badge variant="outline">{row.original.contract_kind.replace(/_/g, ' ')}</Badge> },
          { accessorKey: 'months_active', header: 'Active months' },
          { accessorKey: 'payout_months', header: 'Payout months' },
          { accessorKey: 'annual_salary_minor_units', header: 'Annual salary', cell: ({ row }: { row: { original: { annual_salary_minor_units: string; currency: string } } }) => formatPayrollMoney(row.original.annual_salary_minor_units, row.original.currency) },
          { accessorKey: 'academic_year_start', header: 'AY', cell: ({ row }: { row: { original: { academic_year_start: string; academic_year_end: string } } }) => `${formatDate(row.original.academic_year_start)} → ${formatDate(row.original.academic_year_end)}` },
        ] as never}
        empty={<EmptyState icon={GraduationCap} title="No academic contracts yet" description="Set up a 9-month contract for an instructor and the engine spreads the payout across the full 12 months." />}
      />
    </Page>
  )
}

export function UnionsPage() {
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; name: string; local_number: string | null; sector: string | null; cba_count: number }> }>('/api/payroll/industry/union/unions')
  const rows = data?.data ?? []
  return (
    <Page title="Unions & CBAs" description="Unions, collective bargaining agreements, pay scales, dues remittance, and seniority rosters.">
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'name', header: 'Union' },
          { accessorKey: 'local_number', header: 'Local' },
          { accessorKey: 'sector', header: 'Sector' },
          { accessorKey: 'cba_count', header: 'CBAs' },
        ] as never}
        empty={<EmptyState icon={Users2} title="No unions configured" description="Add a union with its local number, then upload its collective bargaining agreement to drive pay scales + dues remittance." />}
      />
    </Page>
  )
}

export function MigrationImporterPage() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const { user } = useAuth()
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; source_system: string; run_kind: string; status: string; rows_total: number; rows_imported: number; rows_skipped: number; rows_failed: number; created_at: string; completed_at: string | null }> }>('/api/payroll/migration/runs')
  const rows = data?.data ?? []
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ source_system: 'gusto', run_kind: 'employees', csv: '' })
  const [busy, setBusy] = useState(false)

  function parseCsv(text: string): Record<string, string>[] {
    const lines = text.trim().split(/\r?\n/)
    if (lines.length < 2) return []
    const header = lines[0].split(',').map((s) => s.trim().toLowerCase())
    return lines.slice(1).map((line) => {
      const cells = line.split(',')
      const obj: Record<string, string> = {}
      header.forEach((h, i) => { obj[h] = (cells[i] || '').trim() })
      return obj
    })
  }

  async function startImport() {
    setBusy(true)
    try {
      const parsed = parseCsv(form.csv)
      const create = await api.post<{ data: { id: string } }>('/api/payroll/migration/runs', {
        source_system: form.source_system,
        run_kind: form.run_kind,
        uploaded_filename: 'inline-paste.csv',
        rows: parsed,
      })
      await api.post(`/api/payroll/migration/runs/${create.data.id}/dry-run`, { rows: parsed })
      const commit = await api.post<{ data: { imported: number; skipped: number; failed: number } }>(`/api/payroll/migration/runs/${create.data.id}/commit`, { rows: parsed })
      toast({ title: 'Import committed', description: `${commit.data.imported} imported · ${commit.data.skipped} skipped · ${commit.data.failed} failed` })
      setOpen(false); setForm({ ...form, csv: '' })
      if (user?.tenantId) qc.invalidateQueries({ queryKey: tk(user.tenantId, '/api/payroll/migration/runs') })
    } catch (err) {
      toast({ title: 'Import failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page
      title="Migration Importer"
      description="Bring data over from ADP, Workday, Gusto, Paychex, QuickBooks Payroll, SAP, Oracle, Rippling, Justworks, Xero, Sage. Dry-run validates before commit."
      actions={<Button onClick={() => setOpen(true)}><Upload className="mr-2 size-4" /> New import</Button>}
    >
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'source_system', header: 'Source', cell: ({ row }: { row: { original: { source_system: string } } }) => <Badge variant="outline">{row.original.source_system.toUpperCase()}</Badge> },
          { accessorKey: 'run_kind', header: 'Kind' },
          { accessorKey: 'rows_total', header: 'Total rows' },
          { accessorKey: 'rows_imported', header: 'Imported' },
          { accessorKey: 'rows_skipped', header: 'Skipped' },
          { accessorKey: 'rows_failed', header: 'Failed' },
          { accessorKey: 'status', header: 'Status', cell: ({ row }: { row: { original: { status: string } } }) => <Badge variant={row.original.status === 'completed' ? 'success' : row.original.status === 'failed' ? 'danger' : 'warning'}>{row.original.status}</Badge> },
          { accessorKey: 'created_at', header: 'Started', cell: ({ row }: { row: { original: { created_at: string } } }) => formatDate(row.original.created_at) },
        ] as never}
        empty={<EmptyState icon={Upload} title="No imports yet" description="Click 'New import' to bring employees / contractors / prior YTD balances over from your previous payroll system." action={<Button onClick={() => setOpen(true)}>New import</Button>} />}
      />
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="New migration import"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={startImport} disabled={busy || !form.csv.trim()}>{busy ? 'Importing…' : 'Run import'}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Source system"
            control={
              <select value={form.source_system} onChange={(e) => setForm({ ...form, source_system: e.target.value })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="adp">ADP</option>
                <option value="workday">Workday</option>
                <option value="gusto">Gusto</option>
                <option value="paychex">Paychex</option>
                <option value="quickbooks">QuickBooks Payroll</option>
                <option value="sap">SAP</option>
                <option value="oracle">Oracle</option>
                <option value="rippling">Rippling</option>
                <option value="justworks">Justworks</option>
                <option value="xero">Xero</option>
                <option value="xero_payroll">Xero Payroll</option>
                <option value="sage">Sage</option>
                <option value="generic_csv">Generic CSV</option>
              </select>
            }
          />
          <FormField
            label="What are you importing?"
            control={
              <select value={form.run_kind} onChange={(e) => setForm({ ...form, run_kind: e.target.value })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="employees">Employees (first_name, last_name, hire_date, email, …)</option>
                <option value="contractors">Contractors (name, email, tax_id_type, country)</option>
                <option value="prior_ytd">Prior YTD balances (employee_number, tax_year, bucket, amount)</option>
                <option value="vendors">Vendors</option>
                <option value="customers">Customers</option>
                <option value="time_attendance">Time & attendance</option>
                <option value="benefits">Benefits enrollments</option>
              </select>
            }
          />
          <FormField
            label="Paste CSV here"
            hint="First row = header. Use the exact column names listed above for each kind."
            control={
              <textarea
                className="h-40 w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
                value={form.csv}
                onChange={(e) => setForm({ ...form, csv: e.target.value })}
                placeholder={'first_name,last_name,hire_date,email\nJane,Doe,2024-01-15,jane@acme.com'}
              />
            }
          />
        </div>
      </Modal>
    </Page>
  )
}

export function FirmPracticePage() {
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; client_business_name: string; client_country: string; relationship_kind: string; engagement_start: string; is_active: boolean }> }>('/api/firm/clients')
  const rows = data?.data ?? []
  return (
    <Page title="Firm Practice" description="Manage client tenants (CPA firms, payroll providers). Each client is a separate Bokapsys tenant; you switch into them with consent.">
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'client_business_name', header: 'Client' },
          { accessorKey: 'client_country', header: 'Country' },
          { accessorKey: 'relationship_kind', header: 'Engagement', cell: ({ row }: { row: { original: { relationship_kind: string } } }) => <Badge variant="outline">{row.original.relationship_kind.replace(/_/g, ' ')}</Badge> },
          { accessorKey: 'engagement_start', header: 'Since', cell: ({ row }: { row: { original: { engagement_start: string } } }) => formatDate(row.original.engagement_start) },
          { accessorKey: 'is_active', header: 'Active', cell: ({ row }: { row: { original: { is_active: boolean } } }) => row.original.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="secondary">Off-boarded</Badge> },
        ] as never}
        empty={<EmptyState icon={Briefcase} title="No client tenants linked" description="Client linkage requires consent from the client tenant before the engagement appears here." />}
      />
    </Page>
  )
}

export function WebhooksPage() {
  const { toast } = useToast()
  const { user } = useAuth()
  const qc = useQueryClient()
  const { data, isLoading } = useTenantQuery<{ data: Array<{ id: string; url: string; description: string | null; is_active: boolean; event_filters: string[]; created_at: string; signing_secret_preview: string }> }>('/api/webhooks/endpoints')
  const deliveries = useTenantQuery<{ data: Array<{ id: string; event_type: string; attempt: number; status: string; response_status: number | null; created_at: string; delivered_at: string | null; url: string }> }>('/api/webhooks/deliveries')
  const rows = data?.data ?? []
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ url: '', description: '', event_filters: '' })
  async function save() {
    try {
      const r = await api.post<{ data: { signing_secret: string } }>('/api/webhooks/endpoints', {
        url: form.url, description: form.description,
        event_filters: form.event_filters.split(',').map((s) => s.trim()).filter(Boolean),
      })
      toast({ title: 'Webhook created', description: `Signing secret: ${r.data.signing_secret} - store it now, won't be shown again.` })
      setOpen(false)
      setForm({ url: '', description: '', event_filters: '' })
      if (user?.tenantId) qc.invalidateQueries({ queryKey: tk(user.tenantId, '/api/webhooks/endpoints') })
    } catch (err) {
      toast({ title: 'Failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    }
  }
  return (
    <Page
      title="Webhooks"
      description="POST a JSON payload to your URL when domain events fire (payroll.run.posted, tax.filing.signed, etc). HMAC-SHA256 signed; retries with exponential backoff."
      actions={<Button onClick={() => setOpen(true)}><Plus className="mr-2 size-4" /> Add endpoint</Button>}
    >
      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Endpoints</h2>
          {isLoading ? <p>Loading…</p> : rows.length === 0 ? (
            <EmptyState icon={Upload} title="No webhook endpoints" description="Add an endpoint to receive event notifications from Bokapsys." />
          ) : (
            <DataTable
              rows={rows}
              columns={[
                { accessorKey: 'url', header: 'URL' },
                { accessorKey: 'description', header: 'Description' },
                { accessorKey: 'event_filters', header: 'Filters', cell: ({ row }: { row: { original: { event_filters: string[] } } }) => (row.original.event_filters || []).map((e) => <Badge key={e} variant="outline" className="mr-1">{e}</Badge>) },
                { accessorKey: 'signing_secret_preview', header: 'Secret', cell: ({ row }: { row: { original: { signing_secret_preview: string } } }) => <code className="text-xs">{row.original.signing_secret_preview}</code> },
                { accessorKey: 'is_active', header: 'Active', cell: ({ row }: { row: { original: { is_active: boolean } } }) => row.original.is_active ? <Badge variant="success">Yes</Badge> : <Badge variant="secondary">Off</Badge> },
              ] as never}
            />
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Recent deliveries</h2>
          {(deliveries.data?.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No deliveries yet. Events emitted to the outbox dispatch every 30 seconds.</p>
          ) : (
            <DataTable
              rows={deliveries.data?.data ?? []}
              columns={[
                { accessorKey: 'event_type', header: 'Event' },
                { accessorKey: 'url', header: 'URL' },
                { accessorKey: 'attempt', header: 'Attempt' },
                { accessorKey: 'status', header: 'Status', cell: ({ row }: { row: { original: { status: string } } }) => <Badge variant={row.original.status === 'delivered' ? 'success' : row.original.status === 'failed' ? 'danger' : 'warning'}>{row.original.status}</Badge> },
                { accessorKey: 'response_status', header: 'HTTP' },
                { accessorKey: 'created_at', header: 'When', cell: ({ row }: { row: { original: { created_at: string } } }) => formatDate(row.original.created_at) },
              ] as never}
            />
          )}
        </CardContent>
      </Card>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Add webhook endpoint"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField label="URL" placeholder="https://example.com/bokapsys" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <FormField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <FormField
            label="Event filters (comma-separated, empty = all events)"
            placeholder="payroll.run.posted, tax.filing.signed"
            value={form.event_filters}
            onChange={(e) => setForm({ ...form, event_filters: e.target.value })}
          />
        </div>
      </Modal>
    </Page>
  )
}

export function PlatformAdminPayrollHealthPage() {
  const { data, isLoading } = useTenantQuery<{ data: Array<{ pack_kind: string; pack_key: string; pack_version: number; effective_from: string; published_at: string; notes: string | null }> }>('/api/platform/payroll/rule-packs')
  const rows = data?.data ?? []
  return (
    <Page title="Tax Rule Packs (Platform)" description="Federal, state, local, and country payroll/sales tax rule packs. HMAC-signed. Bokapsys staff publish new versions here for instant tenant rollout.">
      <DataTable
        loading={isLoading}
        rows={rows}
        columns={[
          { accessorKey: 'pack_kind', header: 'Kind', cell: ({ row }: { row: { original: { pack_kind: string } } }) => <Badge variant="outline">{row.original.pack_kind.replace(/_/g, ' ')}</Badge> },
          { accessorKey: 'pack_key', header: 'Key' },
          { accessorKey: 'pack_version', header: 'Version' },
          { accessorKey: 'effective_from', header: 'Effective', cell: ({ row }: { row: { original: { effective_from: string } } }) => formatDate(row.original.effective_from) },
          { accessorKey: 'notes', header: 'Notes' },
        ] as never}
        empty={<EmptyState icon={FileText} title="No rule packs" description="Rule packs are seeded via migrations 124/134/135 and re-published annually." />}
      />
    </Page>
  )
}
